import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from "../../supabase";
import Tabs from '../../components/common/Tabs';
import { Chip } from '../../components/common/Chip';
import styles from './Lineup.module.css';
import { ICON_PATHS } from '../../constants/iconPaths';
import { Helmet } from 'react-helmet';
import Button from '../../components/common/Button';
import QuarterOrder from './components/QuarterOrder';
import QuarterCalculator from './components/QuarterCalculator';

// TEAMS 배열은 컴포넌트 외부에 유지
const TEAMS = [
  { id: 'blue', name: '블루', color: '#2563eb', value: '블루' },
  { id: 'red', name: '레드', color: '#dc2626', value: '레드' },
  { id: 'yellow', name: '옐로우', color: '#ca8a04', value: '옐로우' },
];

function Lineup() {
  const { matchId } = useParams();
  const [searchParams] = useSearchParams();
  const teamParam = searchParams.get('team');
  const numberParam = searchParams.get('number');

  const [activeTab, setActiveTab] = React.useState('lineup');
  const [selectedTeam, setSelectedTeam] = React.useState('blue');
  const [matchData, setMatchData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [players, setPlayers] = useState([]);
  const [quarters, setQuarters] = useState([]);
  
  // TABS 배열은 제거하고 컴포넌트 내부로 이동
  const TABS = React.useMemo(() => [
    { id: 'lineup', label: '라인업' },
    ...(matchData?.is_auto_rotation === 1 ? [{ id: 'quarters', label: '쿼터 순서' }] : []),
    { id: 'info', label: '매치 정보' },
  ], [matchData?.is_auto_rotation]);

  React.useEffect(() => {
    const fetchMatchData = async () => {
      if (!matchId) {
        setIsLoading(false);
        return;
      }

      try {
        
        const { data, error } = await supabase
          .from('lineup_match')
          .select(`
            *,
            lineup_match_player (
              id,
              team_name,
              team_number,
              is_late,
              is_lead,
              lineup_user (
                id,
                name,
                level
              )
            )
          `)
          .eq('match_id', matchId)
          .single();


        if (error) {
          throw error;
        }

        setMatchData(data);
      } catch (error) {
        setMatchData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMatchData();
  }, [matchId]);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const { data, error } = await supabase
          .from('lineup_match_player')
          .select(`
            id,
            team_name,
            team_number,
            is_lead,
            is_late,
            lineup_user (
              id,
              name,
              level,
              sex,
              age
            )
          `)
          .eq('match_id', matchId);

        if (error) throw error;

        setPlayers(data);
      } catch (error) {
        console.error('Error fetching players:', error);
      }
    };

    fetchPlayers();
  }, [matchId]);

  // 개인화된 정보가 있는 경우 해당 팀 선택
  React.useEffect(() => {
    if (teamParam) {
      setSelectedTeam(teamParam);
    }
  }, [teamParam]);

  // 10초마다 현재 시간 업데이트
  useEffect(() => {
    // 초기 렌더링 시 즉시 업데이트
    setCurrentTime(new Date());
    
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000); // 10초마다 업데이트
    
    return () => clearInterval(timer);
  }, []);


  // Loading 상태 개선
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  // 에러 상태 개선
  if (!matchData) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.error}>
          <h2>매치 정보를 찾을 수 없습니다.</h2>
          <p>Match ID: {matchId}</p>
          <p>존재하지 않는 매치이거나 접근 권한이 없습니다.</p>
        </div>
      </div>
    );
  }

  const getTeamPlayers = (teamName) => {
    return matchData.lineup_match_player
      .filter(player => player.team_name === teamName)
      .sort((a, b) => (a.team_number || 0) - (b.team_number || 0));
  };

  // 활성화된 팀 목록 구하기
  const activeTeams = TEAMS.filter(team => 
    getTeamPlayers(team.value).length > 0
  );

  // 개인화된 정보 확인
  const personalInfo = teamParam && numberParam
    ? matchData.lineup_match_player.find(
        p => p.team_name === TEAMS.find(t => t.id === teamParam)?.value && 
             p.team_number === parseInt(numberParam)
      )
    : null;

  const getBibImage = (teamId) => {
    const team = TEAMS.find(t => t.id === teamId);
    if (!team) return null;
    return `/asset/bibs_${team.id}.png`;
  };

  const formatSchedule = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${year}년 ${month}월 ${day}일 ${hours}:${minutes}`;
  };

  const getPersonalInfoStyle = (teamName) => {
    switch (teamName) {
      case '레드':
        return {
          backgroundColor: 'var(--red-500)',
          color: 'white'
        };
      case '블루':
        return {
          backgroundColor: 'var(--blue-500)',
          color: 'white'
        };
      case '옐로우':
        return {
          backgroundColor: 'var(--yellow-500)',
          color: 'var(--gray-950)'
        };
      default:
        return {};
    }
  };


  // 레벨 표기 함수 추가
  const getLevelText = (level) => {
    if (level === 0 || level === null || level === undefined) return '루키';
    if (level >= 0.1 && level <= 1.0) return '비기너1';
    if (level >= 1.1 && level <= 1.4) return '비기너2';
    if (level >= 1.5 && level <= 2.0) return '비기너3';
    if (level >= 2.1 && level <= 2.2) return '아마추어1';
    if (level >= 2.3 && level <= 2.4) return '아마추어2';
    if (level >= 2.5 && level <= 2.6) return '아마추어3';
    if (level >= 2.7 && level <= 2.8) return '아마추어4';
    if (level >= 2.9 && level <= 3.0) return '아마추어5';
    if (level >= 3.1 && level <= 3.3) return '세미프로1';
    if (level >= 3.4 && level <= 3.6) return '세미프로2';
    if (level >= 3.7 && level <= 4.0) return '세미프로3';
    if (level >= 4.1 && level <= 5.0) return '프로';
    return level.toString(); // 범위를 벗어나는 경우 숫자 그대로 표시
  };

  // 레벨 분포 계산 함수 수정
  const calculateLevelDistribution = (players) => {
    const levelGroups = {
      '루키': 0,
      '비기너': 0,
      '아마추어': 0,
      '세미프로': 0,
      '프로': 0
    };
    
    let totalLevel = 0;
    let playerCount = 0;
    
    players.forEach(player => {
      const level = player.lineup_user.level;
      
      // 모든 플레이어를 그래프에 표시하기 위한 분류
      if (level === 0 || level === null || level === undefined) {
        levelGroups['루키']++;
      } else if (level >= 0.1 && level <= 2.0) {
        levelGroups['비기너']++;
      } else if (level >= 2.1 && level <= 3.0) {
        levelGroups['아마추어']++;
      } else if (level >= 3.1 && level <= 4.0) {
        levelGroups['세미프로']++;
      } else if (level >= 4.1) {
        levelGroups['프로']++;
      }
      
      // 평균 계산에는 레벨이 존재하고 0보다 큰 경우만 포함
      if (level !== null && level !== undefined && level > 0) {
        totalLevel += parseFloat(level);
        playerCount++;
      }
    });
    
    const averageLevel = playerCount > 0 ? totalLevel / playerCount : 0;
    const averageLevelNumeric = averageLevel.toFixed(1);
    const averageLevelText = getLevelText(parseFloat(averageLevelNumeric));
    
    return {
      distribution: levelGroups,
      averageLevelText,
      averageLevelNumeric,
      validPlayerCount: playerCount // 유효한 플레이어 수 추가
    };
  };

  // 팀 컬러 가져오기
  const getTeamColor = (teamName) => {
    const team = TEAMS.find(t => t.value === teamName);
    if (!team) return 'var(--gray-500)';
    
    switch(team.id) {
      case 'red': return 'var(--red-500)';
      case 'blue': return 'var(--blue-500)';
      case 'yellow': return 'var(--yellow-500)';
      default: return 'var(--gray-500)';
    }
  };

  // 팀 ID로 컬러 가져오기 (라인업 탭에서 사용)
  const getTeamColorById = (teamId) => {
    switch(teamId) {
      case 'red': return 'var(--red-500)';
      case 'blue': return 'var(--blue-500)';
      case 'yellow': return 'var(--yellow-500)';
      default: return 'var(--gray-500)';
    }
  };

  // 팀 선수 목록 렌더링 부분
  const renderTeamPlayers = () => {
    const players = getTeamPlayers(TEAMS.find(t => t.id === selectedTeam)?.value);
    const { distribution, averageLevelText, averageLevelNumeric, validPlayerCount } = calculateLevelDistribution(players);
    
    // 분포에서 최대값 찾기 (그래프 스케일링용)
    const maxCount = Math.max(...Object.values(distribution));
    
    return (
      <>
        <div className={styles.teamStatsContainer}>          
          <div className={styles.levelDistributionContainer}>
            <span className={styles.distributionLabel}>
              <span style={{ color: getTeamColorById(selectedTeam) }}>
                {TEAMS.find(t => t.id === selectedTeam)?.name}팀
              </span>의 평균 레벨은 <b>{averageLevelText}</b>에요.
            </span>
            <div className={styles.verticalBarGraph}>
              {Object.entries(distribution).map(([level, count]) => (
                <div key={level} className={styles.verticalBarContainer}>
                  <div className={styles.verticalBarWrapper}>
                    <div 
                      className={styles.verticalBar} 
                      style={{ 
                        height: maxCount > 0 ? `${(count / maxCount) * 100}%` : '0%',
                        backgroundColor: getTeamColorById(selectedTeam)
                      }}
                    >
                    </div>
                  </div>
                  <div className={styles.verticalBarLabel}>{level}</div>
                  <div className={styles.barValue}>{count > 0 ? count : '-'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className={styles.playerList}>
          {players.map(player => (
            <div key={player.id} className={styles.playerItem}>
              <div className={styles.playerInfoContainer}>
                <div className={styles.playerNumber}>
                  <img 
                    src={getBibImage(selectedTeam)} 
                    alt="조끼" 
                    className={styles.bibImage}
                  />
                  <span className={styles.number}>{player.team_number}</span>
                </div>
                <div className={styles.playerInfo}>
                  <span className={styles.playerName}>
                    {player.lineup_user.name}
                    {personalInfo && personalInfo.lineup_user.id === player.lineup_user.id && (
                      <span className={styles.myBadge}>나</span>
                    )}
                  </span>
                </div>
              </div>
              {isUserLead && ( // 리드 권한이 있는 경우에만 버튼 표시
                <Button
                  key={`late-button-${player.id}-${player.is_late}`}
                  type="fill"
                  style={player.is_late === 1 ? 'primary' : 'secondary'}
                  size="small"
                  onClick={() => handleLateToggle(player.id, player.is_late)}
                  className={styles.lateButton}
                >
                  {player.is_late === 1 ? '지각' : '지각'}
                </Button>
              )}
            </div>
          ))}
        </div>
      </>
    );
  };

  // 쿼터 정보 계산 결과를 받는 함수
  const handleCalculateQuarters = (calculatedQuarters) => {
    setQuarters(calculatedQuarters);
  };

  // 시간 포맷 함수 정의
  const formatTime = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // 지각자 정보 생성
  const latePlayers = players.filter(player => player.is_late === 1);
  const latePlayersDescription = latePlayers.map(player => `${player.team_name} ${player.team_number}번`).join(', ');

  // 매치 정책 설명 생성 함수
  const getMatchPolicyDescription = () => {
    if (!matchData) return '';
    
    // 팀 정보 가져오기
    const teams = TEAMS.filter(team => 
      getTeamPlayers(team.value).length > 0
    );
    
    // 전체 선수 수
    const totalPlayers = teams.reduce((sum, team) => 
      sum + getTeamPlayers(team.value).length, 0
    );
    
    // 매치 타입 확인 및 결정
    let playerPerTeamSetting = matchData.player_per_team;
    
    // 매치 타입이 null이거나 undefined인 경우 참가자 수에 따라 결정
    if (!playerPerTeamSetting) {
      if (totalPlayers <= 12) {
        playerPerTeamSetting = '5vs5';
      } else {
        playerPerTeamSetting = '6vs6';
      }
    }
    
    
    // 매치 타입에 따른 정책 설명
    if (playerPerTeamSetting.trim() === '5vs5') {
      if (totalPlayers >= 13) {
        return '각 쿼터는 13~14분간 진행되며(총 2시간), 3팀이 9쿼터 동안 순환하여 경기합니다.';
      } else if (totalPlayers >= 11) {
        return '각 쿼터는 20분간 진행되며, 2팀이 6쿼터 동안 경기합니다. 각 팀은 쿼터당 1명의 휴식 선수가 있습니다.';
      } else if (totalPlayers === 10) {
        return '각 쿼터는 20분간 진행되며, 2팀이 6쿼터 동안 경기합니다.';
      }
    } else {
      // 6vs6 정책
      if (totalPlayers >= 17) {
        return '각 쿼터는 약 13분간 진행되며(총 2시간), 3팀이 9쿼터 동안 순환하여 경기합니다.';
      } else if (totalPlayers >= 15) {
        return '각 쿼터는 15분간 진행되며, 2팀이 8쿼터 동안 경기합니다. 각 팀은 쿼터당 2명의 휴식 선수가 있습니다.';
      } else if (totalPlayers >= 13) {
        return '각 쿼터는 17분간 진행되며, 2팀이 7쿼터 동안 경기합니다. 각 팀은 쿼터당 1명의 휴식 선수가 있습니다.';
      } else if (totalPlayers === 12) {
        return '각 쿼터는 20분간 진행되며, 2팀이 6쿼터 동안 경기합니다.';
      } else if (totalPlayers >= 10) {
        // 10~11명인 경우 5vs5 정책 적용
        if (totalPlayers === 11) {
          return '각 쿼터는 20분간 진행되며, 2팀이 6쿼터 동안 경기합니다. 각 팀은 쿼터당 1명의 휴식 선수가 있습니다.';
        } else {
          return '각 쿼터는 20분간 진행되며, 2팀이 6쿼터 동안 경기합니다.';
        }
      }
    }
    
    return '각 쿼터는 13~14분간 진행되며(총 2시간), 팀 구성과 인원에 따라 휴식 및 키퍼 순서가 자동으로 배정됩니다.';
  };

  // 현재 사용자가 리드 권한을 가지고 있는지 확인
  const isUserLead = players.some(player => {
    if (player.is_lead === 1) {
      // team_name을 영어로 변환하여 비교
      // teamParam이 null이거나 undefined인 경우 처리
      if (!teamParam) return false;
      
      const normalizedTeamName = player.team_name.toLowerCase()
        .replace('옐로우', 'yellow')
        .replace('블루', 'blue')
        .replace('레드', 'red');
      return normalizedTeamName === teamParam.toLowerCase() &&
             player.team_number === parseInt(numberParam);
    }
    return false;
  });


  // 지각 여부 토글 함수
  const handleLateToggle = async (playerId, currentLateStatus) => {
    if (!isUserLead) {
      return;
    }

    try {
      const newLateStatus = currentLateStatus === 1 ? 0 : 1;

      // 즉시 상태 업데이트 (낙관적 업데이트)
      setPlayers(prevPlayers => {
        const updatedPlayers = prevPlayers.map(player =>
          player.id === playerId ? { ...player, is_late: newLateStatus } : player
        );
      
        // matchData도 같이 업데이트
        setMatchData(prevMatchData => ({
          ...prevMatchData,
          lineup_match_player: prevMatchData.lineup_match_player.map(player =>
            player.id === playerId ? { ...player, is_late: newLateStatus } : player
          )
        }));
      
        return updatedPlayers;
      });

      // 데이터베이스 업데이트
      const { error } = await supabase
        .from('lineup_match_player')
        .update({ is_late: newLateStatus })
        .eq('id', playerId);

      if (error) {
        // 에러 발생 시 원래 상태로 롤백
        setPlayers(prevPlayers => {
          return prevPlayers.map(player =>
            player.id === playerId ? { ...player, is_late: currentLateStatus } : player
          );
        });

        throw error;
      }

    } catch (error) {
      alert('업데이트 중 오류가 발생했습니다.');
    }
  };

  return (
    <>
      <Helmet>
        <title>TEAM:{formatSchedule(matchData.schedule)} {matchData.stadium}</title>
      </Helmet>
    <div className={styles.container}>
      {/* 헤더 */}
      <header className={styles.header}>
        {personalInfo && (
          <div 
            className={styles.personalInfo}
            style={getPersonalInfoStyle(personalInfo.team_name)}
          >
            <h3>TEAM {personalInfo.team_name}</h3>
            <h1>{personalInfo.team_number}</h1>
            <p>오늘은 {personalInfo.team_name}팀 {personalInfo.team_number}번으로 출격!</p>
          </div>
        )}
        <div className={styles.matchInfo}>
          <div className={styles.schedule}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              {ICON_PATHS.time.paths.map((pathData, index) => (
                <path
                  key={index}
                  d={pathData.path}
                  fillRule={pathData.fillRule}
                  clipRule={pathData.clipRule}
                  fill="currentColor"
                />
              ))}
            </svg>
            <span>{formatSchedule(matchData.schedule)} (10분 전 도착)</span>
          </div>
          <div className={styles.stadium}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d={ICON_PATHS.stadium}
                fill="currentColor"
              />
            </svg>
            <span>{matchData.stadium}</span>
          </div>
          <p>팀은 레벨, 관계, 매너, 플레이 스타일을 바탕으로 균형있게 배정되며, 임의로 팀을 변경할 수 없어요.</p>
        </div>
      </header>

      {/* 탭바 */}
      <Tabs
        type="primary"
        tabs={TABS}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {/* 탭 콘텐츠 */}
      <div className={styles.content}>
        {activeTab === 'lineup' ? (
          <section>
            <div className={styles.teamChips}>
              {activeTeams.map(team => (
                <Chip
                  key={team.id}
                  label={team.name}
                  color={team.color}
                  selected={selectedTeam === team.id}
                  onClick={() => setSelectedTeam(team.id)}
                />
              ))}
            </div>
            <div className={styles.teamContent}>
              {renderTeamPlayers()}
            </div>
          </section>
        ) : activeTab === 'quarters' ? (
          <section className={styles.quartersSection}>
            <div className={styles.managerNoticeContainer}>
              <div className={styles.managerAvatar}>
                <img src="https://ihcmlcnktfrrojtkrtbt.supabase.co/storage/v1/object/public/asset/avatar/avatar_manager_v2.png" alt="매니저" className={styles.managerAvatar} />
              </div>
              <div className={styles.managerNotice}>
                {matchData && matchData.is_plaber_match === 1 ? (
                  <p>
                    진행 순서에 관계없이 정시까지 도착하지 않으면 매너 점수가 차감돼요.
                    <br /><br />
                    골이 들어가면 두팀 키퍼를 교체해요.
                  </p>
                ) : (
                  <p>
                    진행 순서에 관계없이 정시까지 도착하지 않으면 매너 점수가 차감돼요.
                  </p>
                )}
              </div>
            </div>
            <QuarterCalculator 
              matchData={matchData} 
              getTeamPlayers={getTeamPlayers} 
              TEAMS={TEAMS} 
              onCalculate={handleCalculateQuarters} 
            />
            {quarters.length > 0 && (
              <QuarterOrder 
                quarters={quarters} 
                currentTime={currentTime} 
                getTeamColor={getTeamColor} 
                formatTime={formatTime} 
              />
            )}
          </section>
        ) : (
          <div className={styles.matchInfoContent}>
            {isUserLead && (
              <section>
                <h3>장비 이용</h3>
                <p style={{marginBottom: '10px'}}>플래버들에게 매치 30분 전 본인의 조끼 번호가 발송돼요. 조끼를 구장에 비치해 플래버들이 본인의 번호를 직접 챙기게 해주세요.</p>
                <Button
                  type="fill"
                  style="primary"
                  size="medium"
                  onClick={() => window.open('https://www.notion.so/plabfootball/c03584e73f6f4ef28e30380ad6ae523d?pvs=4', '_blank', 'noopener,noreferrer')}
                >
                  장비 이용 가이드 보기
                </Button>
              </section>
            )}
            <section>
              <h3>진행 방식</h3>
              <p>오늘 매치는 {activeTeams.length}팀, 총 {matchData.lineup_match_player.length}명 플래버가 참여합니다.</p>
              <p>모두 함께 즐길 수 있도록 팀은 레벨, 성별, 친구 관계를 바탕으로 자동 배정됩니다.</p>
            </section>
            <section>
              <h3>스타디움 메모</h3>
              <p>{matchData.stadium_memo || '등록된 정보가 없습니다.'}</p>
            </section>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default Lineup; 